import React, { Component, Fragment } from 'react';
import { withApollo } from 'react-apollo';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { Formik } from 'formik';
import _last from 'lodash/last';
import _set from 'lodash/set';
import _isEmpty from 'lodash/isEmpty';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import PermitButton from '../../components/Global/Buttons/PermitButton';
import { wasTouched } from '../../utils/Validation';

import { AppContext } from '../../AppProvider';
import LoadingIndicator from '../../components/LoadingIndicator';
import ErrorNotification from '../../components/common/ErrorNotification';
import getShipmentModel from '../../Models/Shipment';

import QuickRateBasicInfo from '../../components/quickRate/BasicInfo';
import QuickRateLocation from '../../components/quickRate/Location';
import ShipmentContents from '../../components/shipment/form/ShipmentContents/ShipmentContents';
import ShipmentFormServices from '../../components/shipment/form/Services';
import ShipmentFormEquipment from '../../components/shipment/form/Equipment';
import ShipmentFormRates from '../../components/shipment/form/Rates';
import { showEquipment } from '../../utils/Shipment';
import combineStyles from '../../utils/combineStyles';
import ButtonStyles from '../../styles/button';
import LayoutStyles from '../../styles/layout';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';
import ShipmentFormSchema from '../shipment/ShipmentForm.schema';
import { SHIPMENT_TYPE } from '../../constants/constants';
import { SHIPMENT_SAVE } from '../../constants/permissions';
import _cloneDeep from 'lodash/cloneDeep';
import { postalCodeById } from '../../graphQL/queries/locationQueries';
import { asyncQuery } from '../../utils/queryHelpers';

const styles = theme => ({
  titleWrapper: {
    borderBottom: '1px solid rgba(12,31,44,0.12)',
    marginBottom: theme.spacing.unit * 4,
    paddingBottom: theme.spacing.unit * 3
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  actionButtons: {
    padding: '0 !important',
    width: 165
  },
  noPaddingTop: {
    paddingTop: '0 !important'
  }
});

export class QuickRateIndex extends Component {
  state = {
    shipment: {
      ...getShipmentModel(),
      pickup: {},
      delivery: {},
      isNew: true
    },
    loading: true,
    error: null
  };

  async componentDidMount() {
    const { client } = this.props;
    const {
      orgInfo: {
        config: { defaultLocationId }
      }
    } = this.context.globalProps;

    let defaultZip = '';
    try {
      if (defaultLocationId) {
        const {
          location: {
            byId: {
              address: { postalCode }
            }
          }
        } = await asyncQuery(client, postalCodeById, { id: defaultLocationId });
        defaultZip = postalCode;
      }
      const shipment = getShipmentModel();
      // we don't want shipmentItems for quick rate
      shipment.handlingUnits[0].shipmentItems = [];

      shipment.pickup = shipment.stops[0] || {};
      if (!_isEmpty(shipment.pickup)) {
        shipment.pickup.locationAddress.postalCode = defaultZip;
      }
      shipment.delivery = _last(shipment.stops) || {};
      shipment.isNew = !shipment.id;

      this.setState({
        shipment,
        defaultZip
      });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  }
  _startShipment = (values, touched) => {
    const { history } = this.props;

    // Determine which Handling Units were actually modified
    // so that the Shipment Form can correctly filter out
    // those that the User has not made any changes to
    values.handlingUnits.forEach((_unit, unitIndex) => {
      const handlingUnitTouched = wasTouched(
        touched,
        `handlingUnits[${unitIndex}]`
      );
      if (handlingUnitTouched) {
        _unit.fromQuickRate = true;
      }
    });

    // Pass the form values off to the New Shipment page
    history.push({
      pathname: '/shipments/new',
      state: {
        shipment: values
      }
    });
  };
  _handleValidateField = async (
    currentField,
    currentValue,
    values,
    setFieldError
  ) => {
    const { t } = this.props;
    const schema = ShipmentFormSchema({ t });
    const valueCopy = { ...values };
    const updatedValues = _set(valueCopy, currentField, currentValue);

    try {
      await schema.validateAt(currentField, updatedValues);
      setFieldError(currentField, null);
    } catch (err) {
      setFieldError(currentField, err.message);
    }
  };

  render() {
    const { t, classes } = this.props;
    const { shipment, defaultZip } = this.state;
    const clonedShipment = _cloneDeep(shipment);
    if (this.state.loading) {
      return <LoadingIndicator />;
    }

    if (this.state.error) {
      return <ErrorNotification message={t('Error loading page')} />;
    }

    return (
      <div>
        <div className={classnames(classes.titleWrapper)}>
          <h1
            className={classnames(
              classes.headline4,
              classes.noMarginBottom,
              classes.title
            )}
          >
            {t('Get Quick Rate')}
          </h1>
        </div>
        <Formik
          enableReinitialize={true}
          validationSchema={{}}
          initialValues={{
            ...clonedShipment,
            services: shipment.requestedServices,
            equipment: shipment.requestedEquipment,
            agreeLegalTerms: false
          }}
          render={({
            values,
            handleChange,
            handleReset,
            setFieldValue,
            setFieldError,
            setErrors,
            handleBlur,
            touched
          }) => (
            <Fragment>
              <form>
                <Grid container spacing={24}>
                  <Grid container item spacing={24}>
                    <Grid item lg={3} xs={12}>
                      <QuickRateBasicInfo
                        values={values}
                        handleChange={handleChange}
                        handleValidateField={(currentField, currentValue) => {
                          this._handleValidateField(
                            currentField,
                            currentValue,
                            values,
                            setFieldError
                          );
                        }}
                      />
                    </Grid>
                    <Grid item lg={9} xs={12}>
                      <Grid container spacing={24}>
                        <Grid item md={6} xs={12}>
                          <QuickRateLocation
                            type="pickup"
                            name={t('Pickup')}
                            defaultZip={defaultZip}
                            values={values}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            handleValidateField={(
                              currentField,
                              currentValue
                            ) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                          />
                        </Grid>
                        <Grid item md={6} xs={12}>
                          <QuickRateLocation
                            type="delivery"
                            name={t('Delivery')}
                            defaultZip={defaultZip}
                            values={values}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            handleValidateField={(
                              currentField,
                              currentValue
                            ) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          classes={{ item: classes.noPaddingBottom }}
                        >
                          <ShipmentContents
                            handlingUnits={values.handlingUnits}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            setFieldValue={setFieldValue}
                            showProduct={false}
                            isQuickRate={true}
                            handleValidateField={(
                              currentField,
                              currentValue
                            ) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                          />
                        </Grid>
                        {values.shipmentModeId &&
                          values.shipmentModeId !== SHIPMENT_TYPE.PARCEL && (
                            <Grid
                              item
                              md={
                                values.shipmentModeId === SHIPMENT_TYPE.LTL
                                  ? 12
                                  : 6
                              }
                              xs={12}
                              classes={{ item: classes.noPaddingTop }}
                            >
                              <ShipmentFormServices
                                values={values}
                                handleChange={handleChange}
                              />
                            </Grid>
                          )}
                        {showEquipment(values.shipmentModeId) && (
                          <Grid
                            item
                            md={6}
                            xs={12}
                            classes={{ item: classes.noPaddingTop }}
                          >
                            <ShipmentFormEquipment
                              handleChange={handleChange}
                              values={values}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <ShipmentFormRates
                            values={values}
                            shipmentRates={values.shipmentRates}
                            selectedShipmentRate={values.selectedShipmentRate}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            touched={touched}
                            setErrors={setErrors}
                            isQuickRate={true}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <div className={classes.actions}>
                  <Button
                    variant="outlined"
                    className={classnames(
                      classes.button,
                      classes.actionButtons
                    )}
                    onClick={handleReset}
                    color={'primary'}
                    classes={{ label: classes.buttonLabel }}
                  >
                    {t('Clear Form')}
                  </Button>
                  <PermitButton
                    onClick={() => this._startShipment(values, touched)}
                    variant="contained"
                    color="primary"
                    className={classnames(
                      classes.button,
                      classes.actionButtons
                    )}
                    classes={{ label: classes.buttonLabel }}
                    permission={SHIPMENT_SAVE}
                  >
                    {t('Start Shipment')}
                  </PermitButton>
                </div>
              </form>
            </Fragment>
          )}
        />
      </div>
    );
  }
}

QuickRateIndex.contextType = AppContext;
const QuickRateIndexWithTranslation = withNamespaces()(QuickRateIndex);
export default withStyles(
  combineStyles(
    ButtonStyles,
    LayoutStyles,
    TypographyStyles,
    SpacingStyles,
    styles
  )
)(withApollo(QuickRateIndexWithTranslation));
